import type { HouseholdFactFindDocument, IsoDate, SinceLastReport } from '@cf/shared';
import { householdFactFindReadSchema, sinceLastReportSchema } from '@cf/shared';
import { API_BASE_URL } from '../apiBase';
import type { HouseholdDataClient, HouseholdFactFindState } from './householdClient';
import {
  FactFindConflictError,
  IMPORT_REVIEW_REGION_REMOVED,
  ImportReviewRegionRemovedError,
  PlanLockedError,
} from './householdClient';
import { sessionExpiryError } from '../sessionExpiry';
import { getTaxRulesBasis } from '../taxRulesBasis';

/**
 * HTTP persistence against the real /api/v1 fact-find routes. Responses are
 * re-validated through the shared contract (both directions, like the API
 * itself); a 409 becomes the typed conflict the builder's save loop handles.
 */
export class ApiHouseholdClient implements HouseholdDataClient {
  constructor(
    private readonly getToken: () => string | null,
    private readonly fixedAsOfDate?: IsoDate,
    private readonly fixedStartYear?: number,
  ) {}

  // No fixed override (the normal case) → the session's selected tax-rules
  // basis, read at CALL time so a basis switch reaches every later request.
  getAsOfDate(): IsoDate {
    return this.fixedAsOfDate ?? getTaxRulesBasis().asOfDate;
  }

  getStartYear(): number {
    return this.fixedStartYear ?? getTaxRulesBasis().startYear;
  }

  private headers(): Record<string, string> {
    const token = this.getToken();
    return {
      'content-type': 'application/json',
      ...(token !== null ? { authorization: `Bearer ${token}` } : {}),
    };
  }

  private url(clientId: string): string {
    return `${API_BASE_URL}/api/v1/clients/${clientId}/household-fact-find`;
  }

  /**
   * Turn a failed response into a message that says WHAT was rejected.
   *
   * The API answers a contract failure with `{ errors: [{ field?, message }] }`,
   * and this client used to discard it and report only "HTTP 422" — which after
   * a 42-item import leaves an adviser with nothing to act on. The commonest
   * cause is a field the running API does not know yet (every wire object is
   * strict, so an unknown key is a hard reject), and naming the field makes that
   * diagnosable in one read instead of one bisect.
   */
  private static async describeFailure(res: Response, action: string): Promise<Error> {
    const sessionErr = sessionExpiryError(res);
    if (sessionErr !== null) return sessionErr;
    let detail = '';
    try {
      const body = (await res.json()) as { errors?: { field?: string; message?: string }[] };
      const errors = (body.errors ?? [])
        .map((e) => (e.field !== undefined ? `${e.field}: ${e.message ?? ''}` : (e.message ?? '')))
        .filter((s) => s.trim().length > 0);
      if (errors.length > 0) {
        detail = ` — ${errors.slice(0, 3).join('; ')}${errors.length > 3 ? ` (+${String(errors.length - 3)} more)` : ''}`;
      }
    } catch {
      // A non-JSON body (a gateway page, say) leaves the status on its own.
    }
    return new Error(`Could not ${action} the fact-find (HTTP ${String(res.status)})${detail}`);
  }

  /**
   * D2.1. A 404 means the client is not reachable under this session, which is
   * the same answer the fact-find load gives; anything else is a real failure
   * and is reported rather than degraded into an empty panel, because an empty
   * panel says "no previous report on file" — a claim about the client.
   */
  async loadSinceLastReport(clientId: string): Promise<SinceLastReport | null> {
    const res = await fetch(`${API_BASE_URL}/api/v1/clients/${clientId}/since-last-report`, {
      headers: this.headers(),
    });
    if (res.status === 404) return null;
    if (!res.ok) {
      throw await ApiHouseholdClient.describeFailure(res, 'load the review comparison for');
    }
    const body = (await res.json()) as { data: unknown };
    return sinceLastReportSchema.parse(body.data);
  }

  async loadFactFind(clientId: string): Promise<HouseholdFactFindState | null> {
    const res = await fetch(this.url(clientId), { headers: this.headers() });
    if (!res.ok) {
      throw await ApiHouseholdClient.describeFailure(res, 'load');
    }
    const body = (await res.json()) as { data: unknown };
    const data = householdFactFindReadSchema.parse(body.data);
    if (data.factFind === null) return null;
    return data.factFind;
  }

  async saveFactFind(
    clientId: string,
    document: HouseholdFactFindDocument,
    expectedVersion: number | null,
  ): Promise<HouseholdFactFindState> {
    const res = await fetch(this.url(clientId), {
      method: 'PUT',
      headers: this.headers(),
      body: JSON.stringify({ document, expectedVersion }),
    });
    if (res.status === 409) {
      /**
       * 409 carries two different facts on this route, and telling an adviser
       * the wrong one is worse than telling them nothing: a version conflict is
       * terminal and asks for a reload, while a locked plan is neither — it is
       * a state somebody chose, and reloading changes nothing. Decided on the
       * error CODE, not the status.
       */
      const body = (await res.json().catch(() => null)) as {
        errors?: { code?: string }[];
      } | null;
      if ((body?.errors ?? []).some((e) => e.code === 'review_locked')) {
        throw new PlanLockedError();
      }
      const current = await this.loadFactFind(clientId).catch(() => null);
      throw new FactFindConflictError(current?.version ?? null);
    }
    if (res.status === 422) {
      // A save that would delete an open tax-region question is refused with
      // its own code; its message says what to do, so it is shown as-is.
      // A clone, so any other 422 still reaches the generic description intact.
      const body = (await (typeof res.clone === 'function' ? res.clone() : res)
        .json()
        .catch(() => null)) as { errors?: { code?: string; message?: string }[] } | null;
      const removed = (body?.errors ?? []).find((e) => e.code === IMPORT_REVIEW_REGION_REMOVED);
      if (removed !== undefined) throw new ImportReviewRegionRemovedError(removed.message ?? '');
    }
    if (!res.ok) {
      throw await ApiHouseholdClient.describeFailure(res, 'save');
    }
    const body = (await res.json()) as { data: unknown };
    const data = householdFactFindReadSchema.parse(body.data);
    if (data.factFind === null) {
      throw new Error('The save response carried no document');
    }
    return data.factFind;
  }
}
